import React, { useMemo, useState } from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
import { GroupOutlined, LockOutlined, MailOutline } from "@libraries/mui/icons";
// endregion External libraries
// region Atoms
import AccessDenied from "@atoms/AccessDenied";
import ActionDialog from "@atoms/ActionDialog";
import Loading from "@atoms/Loading";
// endregion Atoms
// region Hooks
import { useStaffPermissions } from "@hooks/useStaffPermissions";
// endregion Hooks
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages

import AccessDialog from "./AccessDialog";
import { LegacyMigrationMessages } from "./languages/interfaces";
import {
  forSource, translateError, translateLockReason, translateOutcome, translateReason
} from "./messages";
import ChooseCustomer from "./steps/ChooseCustomer";
import Finished from "./steps/Finished";
import Migrating from "./steps/Migrating";
import Reading from "./steps/Reading";
import Review from "./steps/Review";
import Stepper from "./Stepper";
import * as Styled from "./styles";
import useLegacyMigration, { canLock } from "./useLegacyMigration";

/**
 * Assistant that migrates one customer's users from a legacy system — the CRM
 * (TopSys) database or the customer's TopconFLEET — into Identity.
 *
 * It is a wizard rather than a screen full of controls because each operator
 * will run it a handful of times, ever: one question per step and one obvious
 * action. The migration itself runs from here — a batch of users per request —
 * so the screen stays open until it ends.
 */
const Assistant: React.FC = () => {

  // region Hooks
  const { t } = useTranslation();
  const migration = useLegacyMigration();
  // endregion Hooks
  // region States
  const [confirming, setConfirming] = useState<boolean>(false);
  const [managingAccess, setManagingAccess] = useState<boolean>(false);
  /** Kept so the reading step can name the customer before the extraction answers. */
  const [startingName, setStartingName] = useState<string>("");
  // endregion States

  const {
    extraction, lockLegacy, progress, selected, status
  } = migration;

  /** The source of what is on screen: the review's own, once there is one. */
  const source = extraction?.source ?? migration.source;

  const tenantName = extraction?.tenantName ?? startingName;

  const selectedWithoutPassword = useMemo(
    () => (extraction?.candidates ?? [])
      .filter((candidate) => selected.has(candidate.legacyId) && !candidate.inIdentity && !candidate.passwordKept).length,
    [extraction, selected]
  );

  /** Users whose Topcon Legado password this migration will lock. */
  const lockCount = useMemo(
    () => (extraction?.candidates ?? [])
      .filter((candidate) => candidate.eligible && !candidate.legacyLocked && canLock(candidate, extraction?.source ?? "CRM")
        && selected.has(candidate.legacyId) && lockLegacy.has(candidate.legacyId)).length,
    [extraction, lockLegacy, selected]
  );

  /** Names by legacy id, for the per-user report of the last step. */
  const names = useMemo(
    () => new Map((extraction?.candidates ?? []).map((candidate) => [candidate.legacyId, candidate.name])),
    [extraction]
  );

  const start = (tenantId: string) => {
    setStartingName(migration.tenants.find((tenant) => tenant.id === tenantId)?.name ?? "");
    migration.startExtraction(tenantId);
  };

  /** Builds the report CSV client-side from the outcomes of this run. */
  const exportCsv = () => {
    const names = new Map((extraction?.candidates ?? []).map((candidate) => [candidate.legacyId, candidate.name]));

    const header = [
      "email", "nome", "resultado", "detalhe", "modulos", "senhaPreservada", "emailDeReset",
      "legadoBloqueado", "motivoLegado"
    ];
    const rows = migration.outcomes.map((outcome) => [
      outcome.email,
      names.get(outcome.legacyId) ?? "",
      translateOutcome(t, outcome.outcome),
      translateReason(t, outcome.reason).replace(/"/g, "'"),
      outcome.modulesGranted.join(" "),
      String(outcome.passwordPreserved),
      String(outcome.resetEmailSent),
      outcome.legacyLocked ? "sim" : "não",
      translateLockReason(t, outcome.legacyLockReason, source).replace(/"/g, "'")
    ]);

    const csv = [header, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(";")).join("\n");
    const link = document.createElement("a");

    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    link.download = `migracao-${tenantName}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (migration.loading) return <Loading loading />;

  return (
    <Styled.Wrapper>
      <Styled.Content>
        <Styled.Header>
          <Styled.HeaderText>
            <Styled.PageTitle>{t(LegacyMigrationMessages.legacyMigrationTitle)}</Styled.PageTitle>
            <Styled.PageSubtitle>{t(forSource(source, LegacyMigrationMessages.legacyMigrationSubtitle))}</Styled.PageSubtitle>
          </Styled.HeaderText>
          <Styled.GhostButton type="button" onClick={() => setManagingAccess(true)}>
            {t(LegacyMigrationMessages.legacyMigrationManageAccess)}
          </Styled.GhostButton>
        </Styled.Header>

        <Stepper current={migration.step} />

        {migration.error && (
          <Styled.Banner $tone="error">
            {(() => {
              if (migration.error.status === 403) return t(LegacyMigrationMessages.legacyMigrationForbidden);

              // A token that expired overnight is the likeliest failure here, and
              // "could not complete the operation" would send the operator
              // looking for a problem that is one sign-in away.
              if (migration.error.status === 401) return t(LegacyMigrationMessages.legacyMigrationSessionExpired);

              return translateError(t, migration.error.errorCode);
            })()}
          </Styled.Banner>
        )}

        {/* The CRM registry is this environment's; FLEET reads each customer's own API and needs none. */}
        {status && !status.configured && source === "CRM" && (
          <Styled.Banner $tone="warning">
            {t(LegacyMigrationMessages.legacyMigrationNotConfigured, { problem: status.configurationProblem ?? "" })}
          </Styled.Banner>
        )}

        {migration.step === "customer" && (
          <ChooseCustomer
            tenants={migration.tenants}
            loaded={migration.tenantsLoaded}
            busy={migration.busy}
            configured={source === "FLEET" ? status?.fleetAvailable !== false : !!status?.configured}
            source={migration.source}
            onChangeSource={migration.changeSource}
            onStart={start}
          />
        )}

        {migration.step === "reading" && <Reading tenantName={tenantName} source={source} />}

        {migration.step === "review" && extraction && (
          <Review
            extraction={extraction}
            selected={selected}
            lockLegacy={lockLegacy}
            busy={migration.busy}
            onToggle={migration.toggle}
            onToggleMany={migration.toggleMany}
            onToggleLock={migration.toggleLock}
            onToggleLockMany={migration.toggleLockMany}
            onConfirm={() => setConfirming(true)}
            onBack={migration.leave}
          />
        )}

        {migration.step === "migrating" && (
          <Migrating
            tenantName={tenantName}
            progress={progress}
            stopping={migration.stopping}
            onStop={migration.stop}
          />
        )}

        {migration.step === "done" && (
          <Finished
            tenantName={tenantName}
            source={source}
            progress={progress}
            outcomes={migration.outcomes}
            names={names}
            busy={migration.busy}
            onExport={exportCsv}
            onRetry={migration.migrate}
            onLeave={migration.leave}
          />
        )}
      </Styled.Content>

      <ActionDialog
        open={confirming}
        title={t(LegacyMigrationMessages.legacyMigrationConfirmTitle, { tenant: tenantName })}
        onClose={() => setConfirming(false)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => setConfirming(false) },
          {
            text: t(LegacyMigrationMessages.legacyMigrationConfirmAction),
            action: () => { setConfirming(false); migration.migrate(); }
          }
        ]}
      >
        <Styled.ConfirmList>
          <Styled.ConfirmItem>
            <GroupOutlined />
            <span>{t(LegacyMigrationMessages.legacyMigrationConfirmBody, { count: selected.size })}</span>
          </Styled.ConfirmItem>
          <Styled.ConfirmItem>
            <MailOutline />
            <span>{t(forSource(source, LegacyMigrationMessages.legacyMigrationConfirmEmails), { count: selectedWithoutPassword })}</span>
          </Styled.ConfirmItem>
          {/* The one irreversible part of the migration stands out. */}
          {lockCount > 0 && (
            <Styled.ConfirmItem $tone="warning">
              <LockOutlined />
              <span>{t(forSource(source, LegacyMigrationMessages.legacyMigrationConfirmLocks), { count: lockCount })}</span>
            </Styled.ConfirmItem>
          )}
        </Styled.ConfirmList>
        <Styled.ConfirmNote>{t(LegacyMigrationMessages.legacyMigrationConfirmRerun)}</Styled.ConfirmNote>
      </ActionDialog>

      <AccessDialog open={managingAccess} onClose={() => setManagingAccess(false)} />
    </Styled.Wrapper>
  );
};

/**
 * The legacy migration page. Only admins holding the LEGACY_MIGRATION staff
 * permission see it — the same rule as the menu entry and the server, read
 * through the same hook as the notifications page. The assistant is mounted
 * only past this check, so nobody else ever calls its API.
 */
const LegacyMigration: React.FC = () => {

  // region Hooks
  const { t } = useTranslation();
  const {
    canRunLegacyMigration, loading, error, refetch
  } = useStaffPermissions();
  // endregion Hooks

  if (loading) return null;

  if (error) {
    return (
      <Styled.Wrapper>
        <Styled.Content>
          <Styled.Banner $tone="error">
            {t(LegacyMigrationMessages.legacyMigrationAccessLoadError)}
            {" "}
            <Button size="small" onClick={refetch}>{t(LegacyMigrationMessages.legacyMigrationTryAgain)}</Button>
          </Styled.Banner>
        </Styled.Content>
      </Styled.Wrapper>
    );
  }

  if (!canRunLegacyMigration) return <AccessDenied />;

  return <Assistant />;
};

export default LegacyMigration;
